import { ReactNode } from 'react';
import { Box, Container, Grid, Typography, alpha, Stack, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { MessageFilled, ScheduleFilled, EditFilled, ArrowRightOutlined } from '@ant-design/icons';
import { SECTION_PY } from 'const/section';

const VoiceInputSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const primaryColor = '#2aa1af';

    // Hero(LandingTop)のリード文にある「個別支援計画書」と同じマーカー風の強調効果
    const Highlight = ({ children }: { children: ReactNode }) => (
        <Box
            component="span"
            sx={{
                position: 'relative',
                fontWeight: 700,
                display: 'inline-block',
                mx: 0.5,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: 1,
                    width: '100%',
                    height: '5px',
                    background: alpha(primaryColor, 0.2),
                    borderRadius: '4px',
                    filter: 'blur(2px)',
                    zIndex: -1
                }
            }}
        >
            {children}
        </Box>
    );

    const voiceInputData = [
        {
            title: '会議議事録',
            desc: '個別支援計画の見直し会議や保護者面談も、話すだけでAIが議事録化。',
            icon: <MessageFilled />,
        },
        {
            title: '申し送り・朝礼メモ',
            desc: '職員間の申し送りや朝礼の内容も、音声でサクッと記録。日々の気づきが自然に蓄積されます。',
            icon: <ScheduleFilled />,
        },
        {
            title: '日報・外部連携メモ',
            desc: 'ご利用中の請求ソフトの日報入力も、音声で話すだけでテキスト化。コピペするだけで反映できるから、二重入力の手間がなくなります。',
            icon: <EditFilled />,
        }
    ];

    return (
        <Box
            component="section" // SEO: 音声入力セクションであることを定義
            sx={{
                py: SECTION_PY,
                bgcolor: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Container>
                <Grid container spacing={isMobile ? 6 : 0} alignItems="center">

                    {/* 左側：音声入力カードリスト */}
                    <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
                        <Stack spacing={3} component="ul" sx={{ p: 0, listStyle: 'none', pr: { md: 8 } }}>
                            {voiceInputData.map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                                >
                                    <Box
                                        component="li"
                                        sx={{
                                            p: { xs: 3, md: 4 },
                                            borderRadius: '24px',
                                            bgcolor: '#f4f7f7',
                                            border: '1px solid #eef2f2',
                                            display: 'flex',
                                            gap: 3,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateX(10px)',
                                                boxShadow: `0 12px 30px ${alpha(primaryColor, 0.1)}`,
                                                borderColor: alpha(primaryColor, 0.2)
                                            }
                                        }}
                                    >
                                        <Box
                                            aria-hidden="true"
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: '16px',
                                                bgcolor: alpha(primaryColor, 0.08),
                                                color: primaryColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '1.5rem',
                                                flexShrink: 0
                                            }}
                                        >
                                            {item.icon}
                                        </Box>
                                        <Box component="article">
                                            <Typography
                                                variant="h5"
                                                component="h3"
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1,
                                                    fontSize: '1.2rem',
                                                    color: '#333',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                {item.title}
                                                <ArrowRightOutlined aria-hidden="true" style={{ fontSize: '0.9rem', color: alpha(primaryColor, 0.4) }} />
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                component="p"
                                                sx={{
                                                    color: '#666',
                                                    lineHeight: 1.7,
                                                    fontSize: '0.95rem'
                                                }}
                                            >
                                                {item.desc}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </Stack>
                    </Grid>

                    {/* 右側：ビジュアル・メッセージエリア */}
                    <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
                        <Box sx={{ position: 'relative', pl: { md: 8 } }}>
                            <motion.div
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.2, delay: 1.7, ease: 'easeOut' }}
                                aria-hidden="true"
                                style={{
                                    position: 'absolute',
                                    top: -100,
                                    right: -200,
                                    zIndex: 0,
                                    pointerEvents: 'none'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 400,
                                        height: 400,
                                        background: `radial-gradient(circle, ${alpha(primaryColor, isMobile ? 0.3 : 0.4)} 0%, rgba(255,255,255,0) 70%)`,
                                    }}
                                />
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.7 }}
                                style={{ position: 'relative', zIndex: 1 }}
                            >
                                <Typography
                                    variant="caption"
                                    component="span"
                                    sx={{
                                        color: primaryColor,
                                        fontWeight: 900,
                                        letterSpacing: '0.2em',
                                        display: 'block',
                                        mb: 2
                                    }}
                                >
                                    STEP 05 &nbsp;/&nbsp; VOICE INPUT
                                </Typography>
                                <Typography
                                    variant="h2"
                                    component="h2" // SEO: セクションの見出し
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '2rem', md: '2.8rem' },
                                        lineHeight: 1.3,
                                        color: '#1a1a1a',
                                        mb: 4
                                    }}
                                >
                                    <Box component="span" sx={{ color: primaryColor }}>音声入力</Box>で、<br />
                                    日々の記録をもっと手軽に。
                                </Typography>
                                <Typography
                                    component="p"
                                    sx={{ color: '#555', fontSize: '1rem', lineHeight: 1.8 }}
                                >
                                    <Highlight>音声入力</Highlight>したその場から、OHANA AIが内容を整理・記録。会議から日々のちょっとした気づきまで、話すだけで残せます。
                                </Typography>
                            </motion.div>
                        </Box>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
};

export default VoiceInputSection;
